import { defineComponent as _defineComponent } from 'vue'
import { isEmpty, isEqual } from 'lodash'
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'

import { useCurrentUser } from '@/common/useCurrentUser'
import { BusinessUnit } from '@/models/business-unit'
import Job from '@/models/job'
import { CustomData } from '@/models/types/custom-data'
import { CustomFieldsSchema } from '@/models/types/custom-fields-schema'
import User from '@/models/user'
import { prepareCustomDataForSubmission } from '@/utils/field-schema-helper'
import { ContextualizedFormResolver } from '@/utils/forms/contextualized-form-resolver'
import { FormContext } from '@/utils/forms/form-context'
import { resolveFormDataForValidation } from '@/utils/forms/form-resolver'
import { validateFormData } from '@/utils/forms/form-validate'

import CostCenterField from '@/components/common/CostCenterField.vue'
import FormLayout from '@/components/common/FormLayout.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'

import TicketTags from './TicketTags.vue'
import UpdateBanner from './CustomFields/UpdateBanner.vue'

interface Props {
  job: Job
  jobHubId: string
  schema: CustomFieldsSchema
  hasNewerCustomFieldsSchema: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'JobDetailsEdit',
  props: {
    job: null,
    jobHubId: null,
    schema: null,
    hasNewerCustomFieldsSchema: { type: Boolean }
  },
  emits: ["dismiss", "details-updated", "update-schema-button-clicked"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'dismiss')
  (e: 'details-updated')
  (e: 'update-schema-button-clicked')
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  job: Job
  jobHubId: string
  schema: CustomFieldsSchema
  hasNewerCustomFieldsSchema: boolean
}





const details = ref<string>('')
const costCenter = ref<string>('')
const customData = ref<CustomData>({})
const tags = ref<string[]>([])
const saving = ref<boolean>(false)
const isSnackbarVisible = ref<boolean>(false)
const errorSchema = ref<object>({})

const detailsEditForm = ref<typeof VsForm>()

const store = useStore()
const currentUser = useCurrentUser()

const ticketsError = computed<any>(() => store.getters['tickets/error'])
const getUserBySid = computed<(userSid: string) => User | undefined>(
  () => store.getters['users/getBySid']
)
const getBusinessUnitById = computed<
  (id: string) => BusinessUnit | undefined | null
>(() => store.getters['businessUnits/getBusinessUnitById'])

const hasChanges = computed<boolean>(() => {
  return (
    // Only eval the 1st grouping if versioned custom fields schema flag is on
    (props.schema.id && props.schema.id !== props.job.extensionSchemaId) ||
    details.value !== props.job.details ||
    costCenter.value !== props.job.costCenter ||
    !isEqual(customData.value, props.job.customData) ||
    !isEqual(tags.value, props.job.tags)
  )
})

const shouldShowUpdateCustomFieldsBanner = computed<boolean>(() => {
  return (
    props.hasNewerCustomFieldsSchema &&
    props.job.isArchived === false &&
    props.job.extensionSchemaId !== null
  )
})

const contextualizedFormResolver = computed<ContextualizedFormResolver>(() => {
  const hub = getBusinessUnitById.value(props.jobHubId)
  const user = getUserBySid.value(currentUser.value.userSid)

  const context = new FormContext({
    hub,
    user,
    additionalContext: {},
  })
  return new ContextualizedFormResolver(context)
})

onMounted(() => {
  details.value = props.job.details
  costCenter.value = props.job.costCenter
  customData.value = props.job.customData
  tags.value = props.job.tags
})

function updateCostCenter(value: string | null) {
  costCenter.value = value ?? ''
}

function updateCustomData(value: CustomData) {
  customData.value = value
}

function updateSchemaButtonClicked() {
  emit('update-schema-button-clicked')
}

function updateTags(value: string[]) {
  tags.value = value
}

async function updateJob(job: Job) {
  await store.dispatch('jobs/updateJob', job)
}

function isValid() {
  // Depending on your IDE settings you may see warning here for calling `isValid`.
  // It is a false positive as the VsForm is not composition API yet
  return validateCustomFields() && detailsEditForm.value?.isValid()
}

function validateCustomFields() {
  const resolvedSchema = contextualizedFormResolver.value.retrieveSchema(
    props.schema.schema,
    props.schema.schema,
    customData.value
  )
  const resolvedData = resolveFormDataForValidation(customData.value)

  const errors = validateFormData(resolvedSchema, resolvedData)
  errorSchema.value = errors

  return isEmpty(errors)
}

async function save() {
  if (!isValid()) return

  customData.value = prepareCustomDataForSubmission(
    props.schema,
    customData.value
  )

  const updatedJob = Object.assign({}, props.job, {
    details: details.value,
    costCenter: costCenter.value,
    customData: customData.value,
    tags: tags.value,
    extensionSchemaId: props.schema.id!, // always forward-fill
  })

  saving.value = true
  await updateJob(updatedJob)
  saving.value = false

  if (ticketsError.value) {
    isSnackbarVisible.value = true
  } else {
    emit('details-updated')
    tryDismiss(true)
  }
}

/**
 * Dismisses when forced to and when there are no unsaved changes.
 * @param force Force the dismissal
 */
function tryDismiss(force = false) {
  let isSafeToDismiss = force

  if (isSafeToDismiss === false && hasChanges.value === true) {
    isSafeToDismiss = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?'
    )
  }

  if (isSafeToDismiss || hasChanges.value === false) {
    emit('dismiss')
  }
}

return { __sfc: true,props, emit, details, costCenter, customData, tags, saving, isSnackbarVisible, errorSchema, detailsEditForm, store, currentUser, ticketsError, getUserBySid, getBusinessUnitById, hasChanges, shouldShowUpdateCustomFieldsBanner, contextualizedFormResolver, updateCostCenter, updateCustomData, updateSchemaButtonClicked, updateTags, updateJob, isValid, validateCustomFields, save, tryDismiss, CostCenterField, FormLayout, VsButton, VsForm, VsTextInput, VsWrapper, TicketTags, UpdateBanner }
}

})